"use client";

import { useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const categories = [{
  label: "ALL",
  value: "all"
}, {
  label: "SECURITY",
  value: "security"
}, {
  label: "PRIVACY",
  value: "privacy"
}, {
  label: "REVIEWS",
  value: "reviews"
}, {
  label: "COMPANY",
  value: "company"
}];
const fallbackImages = [{
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_encryption_tech.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_encryption_tech.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_encryption_tech.png-wvc-sizes",
  aspect: "aspect-[4/5]"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_global_network.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_global_network.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_global_network.webp-wvc-sizes",
  aspect: "aspect-[4/3]"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_privacy_shield.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_privacy_shield.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_privacy_shield.png-wvc-sizes",
  aspect: "aspect-square"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_secure_connection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_secure_connection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_secure_connection.png-wvc-sizes",
  aspect: "aspect-[16/10]"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_data_protection.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_data_protection.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_data_protection.png-wvc-sizes",
  aspect: "aspect-[4/5]"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_office_security.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_office_security.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_office_security.webp-wvc-sizes",
  aspect: "aspect-[4/3]"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_anonymous_browsing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_anonymous_browsing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_anonymous_browsing.png-wvc-sizes",
  aspect: "aspect-[16/10]"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_server_infrastructure.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_server_infrastructure.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_server_infrastructure.png-wvc-sizes",
  aspect: "aspect-square"
}, {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_mobile_security.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_mobile_security.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/vpn_mobile_security.png-wvc-sizes",
  aspect: "aspect-[4/5]"
}];
const badgeMap = ["FEATURED", "", "TECHNICAL", "", "", "", "FEATURED", "", ""];
function stripHtml(html: string): string {
  if (typeof window === "undefined") {
    return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  }
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
}
function formatDate(dateString?: string): string {
  if (!dateString) return "";
  try {
    const d = new Date(dateString);
    const day = d.getDate().toString().padStart(2, "0");
    const month = d.toLocaleString("en-US", {
      month: "short"
    }).toUpperCase();
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return "";
  }
}
function JournalGrid() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="columns-1 md:columns-2 lg:columns-3 gap-8">
        {[...Array(9)].map((_, i) => <div key={i} data-index={i} className="mb-8 break-inside-avoid">
            <div className="h-72 bg-muted/30 animate-pulse rounded-lg" />
            <div className="mt-4 h-3 w-1/2 bg-muted/30 animate-pulse rounded-lg" />
            <div className="mt-3 h-6 w-3/4 bg-muted/30 animate-pulse rounded-lg" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20">
        <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">
          Unable to load security insights.
        </p>
        <Button variant="ghost" onClick={refetch} className="mt-4 font-mono text-xs uppercase tracking-widest border border-border hover:border-primary hover:text-primary">
          Retry Connection
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-20">
        <p className="font-mono text-sm text-muted-foreground uppercase tracking-wider">
          No security articles found.
        </p>
      </div>;
  }
  return <>
      <div className="columns-1 md:columns-2 lg:columns-3 gap-8">
        {posts.map((post, i) => {
        const fallback = fallbackImages[i % fallbackImages.length];
        const badge = badgeMap[i % badgeMap.length];
        const imgSrc = post.featuredImage || fallback.src;
        const useFallback = !post.featuredImage;
        const dateStr = formatDate(post.date);
        const categoryName = post.categories && post.categories.length > 0 ? post.categories[0].name.toUpperCase() : "SECURITY";
        const authorName = post.author?.name || "VPNGENIX TEAM";
        const readTime = Math.max(3, Math.min(15, Math.floor((post.content?.length || 2000) / 800)));
        const excerpt = post.excerpt ? stripHtml(post.excerpt) : "";
        const showExcerpt = i % 3 !== 1;
        const titleSize = i % 4 === 0 ? "text-2xl md:text-3xl" : "text-xl md:text-2xl";
        return <article data-index={i} key={post.id} className="mb-8 break-inside-avoid group">
              <Link to={post.link || "#"} className="block">
                <div className={`relative overflow-hidden ${fallback.aspect} border border-border rounded-lg`}>
                  {useFallback ? <img src={fallback.src} data-wvc-srcset={fallback.srcset} data-wvc-sizes={fallback.sizes} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" /> : <img src={imgSrc} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]" />}
                  {badge && <span className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-widest bg-primary text-white px-2 py-1 rounded-sm">
                      {badge}
                    </span>}
                </div>

                <div className="pt-5">
                  <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-3">
                    {dateStr} <span className="mx-1 text-primary">·</span> {categoryName}
                  </div>

                  <h3 className={`font-default font-bold ${titleSize} text-foreground leading-tight tracking-tight`}>
                    <span className="bg-[linear-gradient(to_right,var(--primary),var(--primary))] bg-[length:0%_2px] bg-no-repeat bg-left-bottom transition-[background-size] duration-500 group-hover:bg-[length:100%_2px] pb-1">
                      {post.title}
                    </span>
                  </h3>

                  {showExcerpt && excerpt && <p className="mt-3 text-base text-muted-foreground leading-relaxed line-clamp-2">
                      {excerpt}
                    </p>}

                  <div className="mt-5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                    <span className="text-primary mr-1">—</span> BY {authorName.toUpperCase()}
                    <span className="mx-1 text-primary">·</span> {readTime} MIN READ
                  </div>
                </div>
              </Link>
            </article>;
      })}
      </div>

      {hasNext && <div className="flex justify-center mt-16">
          <Button variant="ghost" onClick={async () => await loadMore()} disabled={isRefetching} className="font-mono text-xs uppercase tracking-[0.2em] px-8 py-6 border border-primary text-primary hover:bg-primary hover:text-white rounded-lg transition-all">
            {isRefetching ? "Loading..." : "Load More Insights"}
          </Button>
        </div>}
    </>;
}
export default function Blog() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const wp_query_params: Record<string, any> = {
    post_type: "post",
    per_page: 12,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="5174" id="blog" className="bg-background text-foreground pt-32 pb-32 px-6 md:px-12 lg:px-16">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-20 lg:mb-28">
          <div className="lg:col-span-8">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-primary mb-8">
              <span className="mr-2">—</span>SECURITY INTELLIGENCE
            </div>
            <h1 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl leading-[1.1] tracking-tighter text-foreground">
              Digital Sovereignty Insights<span className="text-primary">.</span>
            </h1>
            <p className="mt-8 text-xl text-muted-foreground leading-relaxed max-w-[600px]">
              Expert analysis on encryption, privacy protocols, and the evolving landscape of global cybersecurity.
            </p>
          </div>

          <div className="lg:col-span-4 flex flex-col gap-6 lg:items-end lg:justify-end">
            <div className="flex flex-wrap gap-x-5 gap-y-3 lg:justify-end">
              {categories.map((cat, i) => {
              const isActive = activeCategory === cat.value;
              return <Button data-index={i} key={cat.value} variant="ghost" onClick={() => setActiveCategory(cat.value)} className={`font-mono text-[11px] uppercase tracking-[0.2em] pb-1 px-0 h-auto transition-colors ${isActive ? "text-foreground border-b-2 border-primary hover:bg-transparent" : "text-muted-foreground hover:text-foreground border-b-2 border-transparent hover:bg-transparent"}`}>
                    {cat.label}
                  </Button>;
            })}
            </div>

            <div className="relative w-full lg:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search articles..." className="pl-9 bg-input border-border font-mono text-xs uppercase tracking-wider placeholder:text-muted-foreground/70 placeholder:normal-case placeholder:tracking-normal rounded-lg h-14" />
            </div>
          </div>
        </div>

        <PostsProvider wp_query={wp_query}>
          <JournalGrid />
        </PostsProvider>
      </div>
    </section>;
}